<template>
  <div class="negotiation-fields">
    <div
      v-for="question in questions"
      :key="question.id"
      class="negotiation-field"
      :class="{ 'negotiation-field--error': errors[question.id] }"
    >
      <template v-if="question.question_type === 'checkbox'">
        <p class="negotiation-field__label">
          {{ question.label }}
          <span v-if="isRequired(question)" class="negotiation-field__required">
            *
          </span>
        </p>
        <label class="negotiation-field__checkbox">
          <input
            type="checkbox"
            :checked="value[question.id] === true"
            :disabled="disabled"
            @change="update(question, $event.target.checked)"
          />
          {{
            question.description ||
              $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.CHECKBOX_YES')
          }}
        </label>
      </template>

      <template v-else-if="question.question_type === 'list'">
        <p class="negotiation-field__label">
          {{ question.label }}
          <span v-if="isRequired(question)" class="negotiation-field__required">
            *
          </span>
        </p>
        <multiselect
          :value="value[question.id] || null"
          :options="question.list_values"
          :multiple="false"
          :allow-empty="true"
          :disabled="disabled"
          :placeholder="
            question.description ||
              $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.SELECT_PLACEHOLDER')
          "
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          selected-label=""
          @input="update(question, $event || '')"
        />
      </template>

      <template v-else-if="question.question_type === 'number'">
        <p class="negotiation-field__label">
          {{ question.label }}
          <span v-if="isRequired(question)" class="negotiation-field__required">
            *
          </span>
        </p>
        <decimal-input
          :value="value[question.id]"
          :placeholder="placeholder(question)"
          :disabled="disabled"
          allow-negative
          :max-fraction-digits="numberFractionDigits"
          @input="update(question, $event)"
        />
      </template>

      <label v-else class="negotiation-field__input">
        <span class="negotiation-field__label">
          {{ question.label }}
          <span v-if="isRequired(question)" class="negotiation-field__required">
            *
          </span>
        </span>
        <input
          :value="value[question.id]"
          :type="inputType(question)"
          :inputmode="inputMode(question)"
          :maxlength="
            question.question_type === 'date' ? null : maxLength(question)
          "
          :placeholder="placeholder(question)"
          :disabled="disabled"
          @input="update(question, $event.target.value)"
        />
      </label>

      <span v-if="errors[question.id]" class="negotiation-field__message">
        {{ errors[question.id] }}
      </span>
    </div>
  </div>
</template>

<script>
import DecimalInput from 'dashboard/components/widgets/forms/DecimalInput.vue';

const INPUT_MODES = {
  link: 'url',
};

const NUMBER_FRACTION_DIGITS = 6;

export default {
  name: 'OpportunityNegotiationFields',
  components: {
    DecimalInput,
  },
  props: {
    questions: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    requiredQuestionIds: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    numberFractionDigits() {
      return NUMBER_FRACTION_DIGITS;
    },
  },
  methods: {
    isRequired(question) {
      return this.requiredQuestionIds.includes(question.id);
    },
    inputType(question) {
      return question.question_type === 'date' ? 'date' : 'text';
    },
    inputMode(question) {
      return INPUT_MODES[question.question_type] || null;
    },
    maxLength(question) {
      return question.question_type === 'link' ? 2048 : 1000;
    },
    placeholder(question) {
      if (question.description) {
        return question.description;
      }
      if (question.question_type === 'link') {
        return this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.LINK_PLACEHOLDER');
      }
      return '';
    },
    update(question, fieldValue) {
      this.$emit('input', { ...this.value, [question.id]: fieldValue });
      this.$emit('field-change', question.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.negotiation-fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
}

.negotiation-field {
  display: flex;
  flex-direction: column;

  input[type='text'],
  input[type='date'] {
    margin-bottom: 0;
  }

  ::v-deep .multiselect,
  ::v-deep .decimal-input {
    margin-bottom: 0;
  }
}

.negotiation-field__input {
  display: flex;
  flex-direction: column;
}

.negotiation-field__label {
  color: var(--s-900);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-smaller);
  overflow-wrap: anywhere;
}

.negotiation-field__required {
  color: var(--r-600);
  margin-left: var(--space-micro);
}

.negotiation-field__checkbox {
  align-items: center;
  display: flex;
  gap: var(--space-small);
  margin: 0;

  input {
    margin: 0;
  }
}

.negotiation-field__message {
  color: var(--r-700);
  font-size: var(--font-size-mini);
  margin-top: var(--space-smaller);
}

.negotiation-field--error {
  input[type='text'],
  input[type='date'],
  ::v-deep .multiselect__tags,
  ::v-deep .decimal-input__field {
    border-color: var(--r-400);
  }
}
</style>
